/**
 * Scales an image using canvas API with high-quality downsampling.
 * Uses a single-step scaling approach for simplicity and reliability.
 */
export function scaleImage(
  file: File,
  maxWidth?: number,
  maxHeight?: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    if (!maxWidth && !maxHeight) {
      // No scaling needed
      resolve(file);
      return;
    }

    const img = new Image();

    img.onload = () => {
      try {
        const { width: targetWidth, height: targetHeight } =
          calculateTargetDimensions(img.width, img.height, maxWidth, maxHeight);

        // If no scaling is needed, return original file
        if (targetWidth === img.width && targetHeight === img.height) {
          resolve(file);
          return;
        }

        // Create canvas and scale image
        // Keep the source MIME type (including PNG/WebP/GIF) so alpha is not
        // flattened by re-encoding as JPEG. Do not fill an opaque background.
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { alpha: true })!;

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Enable high-quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw the scaled image
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const scaledFile = new File([blob], file.name, {
                type: file.type,
                lastModified: file.lastModified,
              });
              resolve(scaledFile);
            } else {
              reject(new Error('Failed to create blob from scaled image'));
            }
          },
          file.type,
          0.9, // Quality setting for JPEG
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
}

/**
 * Calculates target dimensions while maintaining aspect ratio
 */
function calculateTargetDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth?: number,
  maxHeight?: number,
): { width: number; height: number } {
  let targetWidth = originalWidth;
  let targetHeight = originalHeight;

  // Calculate scaling factors for both constraints
  const widthScale = maxWidth ? maxWidth / originalWidth : 1;
  const heightScale = maxHeight ? maxHeight / originalHeight : 1;

  // Use the smaller scaling factor to maintain aspect ratio
  const scale = Math.min(widthScale, heightScale);

  // Only scale down if we need to (scale < 1)
  if (scale < 1) {
    targetWidth = Math.round(originalWidth * scale);
    targetHeight = Math.round(originalHeight * scale);
  }

  return { width: targetWidth, height: targetHeight };
}
